import React from 'react';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import { ArrowRight, Car, Shield, Truck, DollarSign, Check } from 'lucide-react';

const UseCase: React.FC = () => {
  return (
    <div className="min-h-screen bg-slate-950 text-white">
      {/* Ambient Background */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 right-0 w-[800px] h-[800px] bg-gradient-to-br from-gray-800/20 via-gray-700/10 to-transparent rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 left-0 w-[600px] h-[600px] bg-gradient-to-tr from-black via-gray-900/50 to-transparent rounded-full blur-3xl"></div>
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[1000px] h-[1000px] bg-gradient-to-br from-gray-900/10 to-gray-800/10 rounded-full blur-3xl animate-pulse"></div>
      </div>

      <Navigation />

      {/* Main Content */}
      <div className="relative z-10">
        {/* Hero Section */}
        <section className="relative">
          <div className="max-w-6xl mx-auto px-8 pt-32 pb-16">
            <div className="text-center">
              <h1 className="text-6xl lg:text-7xl font-black leading-tight tracking-tighter mb-8">
                <span className="block">Real-World</span>
                <span className="block text-white">
                  Use Cases
                </span>
              </h1>

              <p className="text-xl text-slate-400 leading-relaxed max-w-3xl mx-auto">
                Discover how businesses across industries leverage our vehicle data API
                to streamline operations and drive growth.
              </p>
            </div>
          </div>
        </section>

        {/* Use Cases Grid */}
        <section className="relative py-16">
          <div className="max-w-7xl mx-auto px-8">
            <div className="grid md:grid-cols-2 gap-8 mb-16">
              {/* Auto Dealerships */}
              <div className="bg-white/5 backdrop-blur-sm rounded-3xl p-10 border border-white/10 hover:border-[#AD0000]/30 transition-all">
                <div className="flex items-center space-x-4 mb-6">
                  <div className="w-14 h-14 bg-gradient-to-br from-[#AD0000] to-red-700 rounded-2xl flex items-center justify-center shadow-lg">
                    <Car className="w-7 h-7 text-white" />
                  </div>
                  <h2 className="text-3xl font-black text-white">Auto Dealerships</h2>
                </div>
                <p className="text-lg text-slate-400 leading-relaxed mb-6">
                  Instantly verify vehicle history, ownership, and title status during the sales process.
                  Reduce fraud and improve customer trust with comprehensive VIN lookups.
                </p>
                <ul className="space-y-3 text-slate-400">
                  {[
                    'Real-time VIN verification at point of sale',
                    'Automated title status checks',
                    'Bulk processing for inventory management'
                  ].map((feature, i) => (
                    <li key={i} className="flex items-start">
                      <Check className="w-5 h-5 text-white mr-3 flex-shrink-0 mt-0.5" />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Insurance Companies */}
              <div className="bg-white/5 backdrop-blur-sm rounded-3xl p-10 border border-white/10 hover:border-green-500/30 transition-all">
                <div className="flex items-center space-x-4 mb-6">
                  <div className="w-14 h-14 bg-gradient-to-br from-green-500 to-emerald-600 rounded-2xl flex items-center justify-center shadow-lg">
                    <Shield className="w-7 h-7 text-white" />
                  </div>
                  <h2 className="text-3xl font-black bg-gradient-to-r from-green-400 to-emerald-500 bg-clip-text text-transparent">Insurance Companies</h2>
                </div>
                <p className="text-lg text-slate-400 leading-relaxed mb-6">
                  Streamline underwriting and claims processing with instant access to vehicle data.
                  Reduce processing time from days to seconds.
                </p>
                <ul className="space-y-3 text-slate-400">
                  {[
                    'Automated risk assessment',
                    'Fast claims verification',
                    'Fraud detection and prevention'
                  ].map((feature, i) => (
                    <li key={i} className="flex items-start">
                      <Check className="w-5 h-5 text-green-400 mr-3 flex-shrink-0 mt-0.5" />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Fleet Management */}
              <div className="bg-white/5 backdrop-blur-sm rounded-3xl p-10 border border-white/10 hover:border-blue-500/30 transition-all">
                <div className="flex items-center space-x-4 mb-6">
                  <div className="w-14 h-14 bg-gradient-to-br from-blue-500 to-cyan-600 rounded-2xl flex items-center justify-center shadow-lg">
                    <Truck className="w-7 h-7 text-white" />
                  </div>
                  <h2 className="text-3xl font-black bg-gradient-to-r from-blue-400 to-cyan-500 bg-clip-text text-transparent">Fleet Management</h2>
                </div>
                <p className="text-lg text-slate-400 leading-relaxed mb-6">
                  Manage large vehicle fleets with automated compliance tracking and registration
                  renewals across all 50 states.
                </p>
                <ul className="space-y-3 text-slate-400">
                  {[
                    'Multi-state registration tracking',
                    'Automated renewal notifications',
                    'Compliance reporting and analytics'
                  ].map((feature, i) => (
                    <li key={i} className="flex items-start">
                      <Check className="w-5 h-5 text-blue-400 mr-3 flex-shrink-0 mt-0.5" />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Lenders & Financial Services */}
              <div className="bg-white/5 backdrop-blur-sm rounded-3xl p-10 border border-white/10 hover:border-yellow-500/30 transition-all">
                <div className="flex items-center space-x-4 mb-6">
                  <div className="w-14 h-14 bg-gradient-to-br from-yellow-500 to-orange-500 rounded-2xl flex items-center justify-center shadow-lg">
                    <DollarSign className="w-7 h-7 text-white" />
                  </div>
                  <h2 className="text-3xl font-black bg-gradient-to-r from-yellow-400 to-orange-500 bg-clip-text text-transparent">Lenders & Financial</h2>
                </div>
                <p className="text-lg text-slate-400 leading-relaxed mb-6">
                  Verify collateral value and ownership for auto loans. Protect your investments
                  with accurate, up-to-date vehicle information.
                </p>
                <ul className="space-y-3 text-slate-400">
                  {[
                    'Instant lien and title verification',
                    'Collateral value assessment',
                    'Portfolio risk management'
                  ].map((feature, i) => (
                    <li key={i} className="flex items-start">
                      <Check className="w-5 h-5 text-yellow-400 mr-3 flex-shrink-0 mt-0.5" />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* Stats Section */}
        <section className="relative py-16 border-t border-white/10">
          <div className="max-w-7xl mx-auto px-8">
            <div className="bg-white/5 backdrop-blur-sm rounded-3xl p-12 border border-white/10">
              <h2 className="text-4xl font-black mb-12 text-center">
                Industry <span className="text-white">Performance</span>
              </h2>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
                {[
                  { value: '70%', label: 'Cost Reduction', color: 'from-white to-gray-300' },
                  { value: '95%', label: 'Faster Processing', color: 'from-green-400 to-emerald-500' },
                  { value: '99.9%', label: 'Data Accuracy', color: 'from-blue-400 to-cyan-500' },
                  { value: '24/7', label: 'Availability', color: 'from-yellow-400 to-orange-500' }
                ].map((stat, i) => (
                  <div key={i} className="text-center">
                    <div className={`text-5xl font-black mb-3 bg-gradient-to-r ${stat.color} bg-clip-text text-transparent`}>
                      {stat.value}
                    </div>
                    <div className="text-slate-500 font-medium">{stat.label}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="relative py-32">
          <div className="max-w-4xl mx-auto px-8 text-center">
            <h3 className="text-5xl font-black mb-6">Ready to Transform Your Business?</h3>
            <p className="text-xl text-slate-400 mb-12 max-w-2xl mx-auto">
              Join hundreds of companies already using our API to streamline their operations.
            </p>
            <button className="group px-10 py-5 bg-gradient-to-r from-[#AD0000] to-red-700 hover:from-red-700 hover:to-red-600 text-white font-bold text-lg rounded-xl transition-all shadow-xl shadow-red-500/30 flex items-center space-x-3 mx-auto">
              <span>Get Started</span>
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </button>
          </div>
        </section>
      </div>

      <Footer />

      <style>{`
        @keyframes float {
          0%, 100% {
            transform: translateY(0);
          }
          50% {
            transform: translateY(-20px);
          }
        }
        .animate-float {
          animation: float 3s ease-in-out infinite;
        }
      `}</style>
    </div>
  );
};

export default UseCase;
